import { useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import {
  ErrorOutline,
  KeyResultIcon,
  KeyResultPrivate2,
  ObjectiveIcon,
  PrivateObjective,
} from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { OBJECTIVE_LEVEL_TAGS, OKR_PROGRESS_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const isEllipsisActive = (containerElement) => containerElement?.offsetHeight < containerElement?.scrollHeight;

const TruncatedTextBodyMedium = styled(TextBodyMedium)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TruncatedTextLabel = styled(TextLabel)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #666666;
`;

const renderParentIcon = (type) => {
  if (type === 'OBJECTIVE') {
    return <ObjectiveIcon color="icon.g2" boxSize="14px" mr={2} />;
  }

  return <KeyResultIcon color="icon.g2" boxSize="14px" mr={2} />;
};

const ObjectiveTypeIcon = ({ objectiveType, departmentName }) => (
  <Flex mt="2px" alignItems="center">
    <TagIcon tagId={objectiveType} />
    <TextLabel ml="8px" color="text.g2">
      {OBJECTIVE_LEVEL_TAGS.find(({ value }) => value === objectiveType)?.label}
    </TextLabel>
    {objectiveType === 'DEPARTMENT' && departmentName ? (
      <Flex alignItems="center" marginLeft="6px">
        <Flex background="#666666" height="4px" width="4px" borderRadius="50%" />
        <TextLabel ml="8px" color="text.g2">
          {departmentName}
        </TextLabel>
      </Flex>
    ) : null}
  </Flex>
);

const ObjectiveKRCell = ({ data, isReview }) => {
  const theme = useTheme();
  const { title, type, visibilityType, departmentName, unMasked, parentGoalBasicDetails } = data ?? {};
  const isObjective = type === 'OBJECTIVE';
  const isPrivateOKR = visibilityType === 'PRIVATE';
  const [showTooltip, setShowTooltip] = useState(true);

  const measuredRef = (node) => {
    if (node !== null) {
      const pTag = node.querySelector('.truncated-ellipsis-tooltip');
      setShowTooltip(isEllipsisActive(pTag));
    }
  };

  const unmaskedText = title;

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const maskedText = `You do not have access to this private ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  if (!unMasked) {
    return (
      <Box w="100%">
        <Flex alignItems="flex-start">
          <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
          <Flex ref={measuredRef} ml="6px" flexDir="column" w="100%">
            <Tooltip label={maskedText} isDisabled={!showTooltip} shouldWrapChildren fontSize="14px">
              <TruncatedTextBodyMedium
                style={{ fontStyle: 'italic' }}
                className="highlight-able-text truncated-ellipsis-tooltip"
                mb={isObjective ? '1px' : 0}
              >
                {maskedText}
              </TruncatedTextBodyMedium>
            </Tooltip>
          </Flex>
        </Flex>
      </Box>
    );
  }

  /* 
    find and report errors in okr
    1. if tracking type is dependent and okr has no children
    2. if tracking type is derived_sum and children are not summable.
  */

  const isDerivedSum = OKR_PROGRESS_TYPES.DERIVED_SUM === data?.progressType;
  const isDerivedAvg = OKR_PROGRESS_TYPES.DERIVED_AVG === data?.progressType;
  const okrHasNoChildren = data?.ifGoalChildExists === false;
  const areChildSummable = data?.areChildSummable !== false;

  const isTrackedAsDependentWithNoChildren = (isDerivedAvg || isDerivedSum) && okrHasNoChildren;

  const isTrackedAsDerivedSumWithNonSummableChildren = isDerivedSum && !areChildSummable;

  const hasError = isTrackedAsDependentWithNoChildren || isTrackedAsDerivedSumWithNonSummableChildren;

  let errorMessage = '';

  if (hasError && isTrackedAsDependentWithNoChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent' but has no child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )}`;
  }

  if (hasError && isTrackedAsDerivedSumWithNonSummableChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent - Sum' but has child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} whose metric/measures are not summable`;
  }

  return (
    <>
      <Box w="100%">
        {isReview && (
          <Tooltip label={unmaskedText} isDisabled={!showTooltip} shouldWrapChildren fontSize="14px">
            <Flex>
              {renderParentIcon(parentGoalBasicDetails?.goalType)}
              <TruncatedTextLabel
                className="highlight-able-text truncated-ellipsis-tooltip"
                mb={isObjective ? '1px' : 0}
              >
                {parentGoalBasicDetails?.goalTitle ?? 'No parent'}
              </TruncatedTextLabel>
            </Flex>
          </Tooltip>
        )}

        <Flex alignItems="flex-start">
          {isObjective ? (
            isPrivateOKR ? (
              <PrivateObjective color="#7651BC" boxSize="24px" />
            ) : (
              <Objective color="#7651BC" boxSize="24px" />
            )
          ) : (
            <>
              {isPrivateOKR ? (
                <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
              ) : (
                <KeyResult color="#4657AE" boxSize="24px" />
              )}
            </>
          )}

          <Flex ml="6px" flexDir="column" w="90%" ref={measuredRef} flexGrow={1}>
            <Flex w="100%" flexWrap="nowrap">
              {hasError ? (
                <Tooltip label={errorMessage} fontSize="14px">
                  <Box as="span" lineHeight="20px">
                    <ErrorOutline
                      style={{ marginRight: '8px', marginBottom: '2px' }}
                      boxSize="16px"
                      color={theme.colors.alerts.negative}
                    />
                  </Box>
                </Tooltip>
              ) : null}
              <Tooltip label={unmaskedText} isDisabled={!showTooltip} shouldWrapChildren fontSize="14px">
                <TruncatedTextBodyMedium
                  className="highlight-able-text truncated-ellipsis-tooltip"
                  mb={isObjective ? '1px' : 0}
                >
                  {title}
                </TruncatedTextBodyMedium>
              </Tooltip>
            </Flex>

            {isObjective && <ObjectiveTypeIcon objectiveType={data?.objectiveType} departmentName={departmentName} />}
          </Flex>
        </Flex>
      </Box>
      {/* {isObjective &&
        column.colDef?.headerName !== loggedInEmployeeOrganizationSettings?.objectivesName && (
          <FloatingButtons data={data} />
        )} */}
    </>
  );
};

export { ObjectiveKRCell };
